"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Check, CheckCircle, Globe, Home, Lock } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import DeleteConfirmationModal from "@/components/tailwind/ui/deleteConfirmationModal";
import type { WorkArea } from "@/types/workarea";
import type { Members } from "@/types/workspace";
import { MemberHoverAvatar } from "@/components/tailwind/ui/MemberHoverAvatar";
import { DropdownMenu } from "@/components/tailwind/ui/dropdown-menu";
import type { DropdownMenuItemProps } from "@/components/tailwind/ui/dropdown-menu";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { postWithAuth } from "@/lib/api-helpers";

interface GeneralTabProps {
  workArea: WorkArea;
  ownerMember?: Members;
  isOwner: boolean;
  isMember: boolean;
  workspaceName: string;
  // When the parent workspace is public, the teamspace member permission is
  // locked to the default (editor) and cannot be changed by anyone.
  memberAccessLocked?: boolean;
  onDescriptionChange: (description: string) => Promise<void>;
  onAccessLevelChange: (newAccessLevel: "open" | "private") => Promise<void>;
  onMemberAccessChange: (newMemberAccess: "full" | "edit" | "view") => Promise<void>;
  onLeave: () => Promise<void>;
  onArchive: () => Promise<void>;
}

export default function GeneralTab({
  workArea,
  ownerMember,
  isOwner,
  isMember,
  workspaceName,
  memberAccessLocked = false,
  onDescriptionChange,
  onAccessLevelChange,
  onMemberAccessChange,
  onLeave,
  onArchive,
}: GeneralTabProps) {
  const { user } = useAuth();
  const [description, setDescription] = useState(workArea.description || "");
  const [isAccessDropdownOpen, setIsAccessDropdownOpen] = useState(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const accessButtonRef = useRef<HTMLButtonElement>(null);
  const accessRowRef = useRef<HTMLDivElement>(null);
  const [isMemberAccessDropdownOpen, setIsMemberAccessDropdownOpen] = useState(false);
  const memberAccessButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setDescription(workArea.description || "");
  }, [workArea.description]);

  const accessLabel = workArea.accessLevel === "open" ? "Open" : "Private";
  const accessDescription =
    workArea.accessLevel === "open"
      ? "Anyone can see and join this workarea"
      : "Only invited members can access this workarea";

  // Teamspace-wide member permission (Notion-style). Defaults to "edit".
  const memberAccessValue = workArea.memberAccess ?? "edit";
  const memberAccessLabel =
    memberAccessValue === "full"
      ? "Full access"
      : memberAccessValue === "view"
        ? "Can view"
        : "Can edit";
  // In a public workspace the member permission is locked to editor ("Can edit"),
  // so show that regardless of any stored value.
  const displayMemberAccessLabel = memberAccessLocked ? "Can edit" : memberAccessLabel;

  const handleAccessChange = async (newAccessLevel: "open" | "private") => {
    if (!isOwner) return;
    try {
      await onAccessLevelChange(newAccessLevel);
      setIsAccessDropdownOpen(false);
      toast.success("Access level updated");
    } catch (err) {
      console.error("Failed to update access level", err);
      toast.error("Failed to update access level");
    }
  };

  const accessOptions: Array<{
    id: "open" | "private";
    label: string;
    description: string;
    icon: typeof Globe;
  }> = [
    {
      id: "open",
      label: "Open",
      description: "Anyone can see and join this workarea",
      icon: Globe,
    },
    {
      id: "private",
      label: "Private",
      description: "Only invited members can access this workarea",
      icon: Lock,
    },
  ];

  const handleMemberAccessChange = async (newMemberAccess: "full" | "edit" | "view") => {
    if (!isOwner) return;
    try {
      await onMemberAccessChange(newMemberAccess);
      setIsMemberAccessDropdownOpen(false);
      toast.success("Member access updated");
    } catch (err) {
      console.error("Failed to update member access", err);
      toast.error("Failed to update member access");
    }
  };

  const memberAccessMenuItems: DropdownMenuItemProps[] = useMemo(
    () => [
      {
        id: "full",
        label: "Full access",
        onClick: () => handleMemberAccessChange("full"),
        selected: memberAccessValue === "full",
        rightElement:
          memberAccessValue === "full" ? <CheckCircle className="w-4 h-4" /> : undefined,
      },
      {
        id: "edit",
        label: "Can edit",
        onClick: () => handleMemberAccessChange("edit"),
        selected: memberAccessValue === "edit",
        rightElement:
          memberAccessValue === "edit" ? <CheckCircle className="w-4 h-4" /> : undefined,
      },
      {
        id: "view",
        label: "Can view",
        onClick: () => handleMemberAccessChange("view"),
        selected: memberAccessValue === "view",
        rightElement:
          memberAccessValue === "view" ? <CheckCircle className="w-4 h-4" /> : undefined,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberAccessValue],
  );

  const handleDescriptionBlur = async () => {
    const trimmed = description.trim();
    if (trimmed === (workArea.description || "")) return;
    try {
      await onDescriptionChange(trimmed);
    } catch (err) {
      console.error("Failed to update description", err);
      toast.error("Failed to update description");
    }
  };

  const handleConfirmArchive = async () => {
    setIsArchiving(true);
    try {
      await onArchive();
      toast.success("Work area archived");
    } catch (err) {
      console.error("Failed to archive work area", err);
      toast.error("Failed to archive work area");
    } finally {
      setIsArchiving(false);
      setIsArchiveConfirmOpen(false);
    }
  };

  const handleConfirmLeave = async () => {
    if (!user?.email) return;
    setIsLeaving(true);
    try {
      const response = await postWithAuth("/api/workarea/remove-member", {
        workAreaId: workArea._id,
        memberId: user.email,
      });
      if (response && !(response as { isError?: boolean }).isError) {
        toast.success("You left the work area");
        await onLeave();
      } else {
        throw new Error("Failed to leave");
      }
    } catch (err) {
      console.error("Failed to leave work area", err);
      toast.error("Failed to leave work area");
    } finally {
      setIsLeaving(false);
      setIsLeaveConfirmOpen(false);
    }
  };

  const ownerInitial =
    ownerMember?.userName?.charAt(0).toUpperCase() ||
    ownerMember?.userEmail?.charAt(0).toUpperCase() ||
    "U";

  return (
    <>
      <div className="px-6 py-5 space-y-6">
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            disabled={!isOwner}
            placeholder="No description"
            className="w-full px-3 py-2 text-sm border border-component rounded-md bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
          />
        </div>

        {/* Access permission card */}
        <div className="rounded-lg border border-component overflow-hidden">
          <div ref={accessRowRef} className="flex items-center gap-3 px-3 py-2.5">
            {workArea.accessLevel === "open" ? (
              <Globe className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
            ) : (
              <Lock className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{accessLabel}</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                {accessDescription}
              </div>
            </div>
            {isOwner && (
              <button
                ref={accessButtonRef}
                onClick={() => setIsAccessDropdownOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 rounded transition-colors flex-shrink-0"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="border-t border-component" />

          {/* Teamspace owners row */}
          <div className="flex items-center gap-3 px-3 py-2.5">
            <MemberHoverAvatar
              name={ownerMember?.userName}
              email={ownerMember?.userEmail}
              userId={ownerMember?.userId}
              wrapperClassName="flex-shrink-0"
              className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 text-[10px] font-medium text-white"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-900 dark:text-zinc-100">Workarea owners</div>
            </div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400 flex-shrink-0">
              Full access
            </div>
          </div>

          <div className="border-t border-component" />

          {/* Teamspace members row */}
          <div className="flex items-center gap-3 px-3 py-2.5">
            <Home className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-900 dark:text-zinc-100">Workarea members</div>
            </div>
            {isOwner && !memberAccessLocked ? (
              <button
                ref={memberAccessButtonRef}
                onClick={() => setIsMemberAccessDropdownOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 rounded transition-colors"
              >
                <span>{memberAccessLabel}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            ) : (
              <div
                className="text-sm text-zinc-500 dark:text-zinc-400 flex-shrink-0"
                title={memberAccessLocked ? "Locked to Can edit in public workspaces" : undefined}
              >
                {displayMemberAccessLabel}
              </div>
            )}
          </div>
        </div>

        {/* Manage teamspace */}
        <div>
          <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
            Manage workarea
          </div>

          {/* Leave teamspace */}
          <div className="flex items-center justify-between py-3">
            <div className="min-w-0 flex-1 pr-4">
              <div className="text-sm text-zinc-900 dark:text-zinc-100">Leave workarea</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Removes workarea from your sidebar
              </div>
            </div>
            <button
              onClick={() => setIsLeaveConfirmOpen(true)}
              disabled={!isMember || isOwner}
              className={cn(
                "inline-flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium border transition-colors flex-shrink-0",
                !isMember || isOwner
                  ? "border-component text-zinc-400 dark:text-zinc-500 cursor-not-allowed"
                  : "border-component text-zinc-900 dark:text-zinc-100 hover-component ",
              )}
            >
              Leave workarea
            </button>
          </div>

          {/* Archive teamspace */}
          {/* <div className="flex items-center justify-between py-3">
            <div className="min-w-0 flex-1 pr-4">
              <div className="text-sm text-zinc-900 dark:text-zinc-100">Archive teamspace</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Removes teamspace from the sidebar for all members
              </div>
            </div>
            <button
              onClick={() => setIsArchiveConfirmOpen(true)}
              disabled={!isOwner}
              className={cn(
                "inline-flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium border transition-colors flex-shrink-0",
                !isOwner
                  ? "border-red-200 dark:border-red-900/30 text-red-400 dark:text-red-500/70 cursor-not-allowed"
                  : "border-red-200 dark:border-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20",
              )}
            >
              Archive teamspace
            </button>
          </div> */}
        </div>
      </div>

      {/* Access Level Dropdown */}
      {isAccessDropdownOpen && isOwner && (
        <AnchoredPopover
          anchor={accessRowRef}
          matchAnchorWidth
          // Above the WorkAreaViewModal shell (z-[11000]).
          zIndex={12000}
          onClose={() => setIsAccessDropdownOpen(false)}
          className="bg-white dark:bg-zinc-900 shadow-lg border border-component rounded-lg overflow-hidden py-1"
        >
          {accessOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = workArea.accessLevel === option.id;
            return (
              <button
                key={option.id}
                onClick={() => handleAccessChange(option.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                  "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                  isSelected && "bg-zinc-50 dark:bg-zinc-800/60",
                )}
              >
                <Icon className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {option.label}
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                    {option.description}
                  </div>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-zinc-900 dark:text-zinc-100 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </AnchoredPopover>
      )}

      {/* Member Access Dropdown */}
      {isMemberAccessDropdownOpen && isOwner && (
        <AnchoredPopover
          anchor={memberAccessButtonRef}
          // Above the WorkAreaViewModal shell (z-[11000]).
          zIndex={12000}
          onClose={() => setIsMemberAccessDropdownOpen(false)}
          className="bg-white dark:bg-zinc-900 shadow-lg border border-component rounded-md min-w-[160px]"
        >
          <DropdownMenu items={memberAccessMenuItems} />
        </AnchoredPopover>
      )}

      {/* Archive confirmation */}
      {isArchiveConfirmOpen && typeof window !== "undefined" && (
        <DeleteConfirmationModal
          header="Archive Teamspace"
          title="Archive teamspace?"
          message={`This will remove ${workArea.name} from the sidebar for all members. You can't undo this action.`}
          isOpen={isArchiveConfirmOpen}
          isDeleting={isArchiving}
          onCancel={() => {
            if (!isArchiving) setIsArchiveConfirmOpen(false);
          }}
          onConfirm={handleConfirmArchive}
        />
      )}

      {/* Leave confirmation */}
      {isLeaveConfirmOpen && typeof window !== "undefined" && (
        <DeleteConfirmationModal
          header="Leave Teamspace"
          title="Leave teamspace?"
          message={`This will remove ${workArea.name} from your sidebar.`}
          isOpen={isLeaveConfirmOpen}
          isDeleting={isLeaving}
          onCancel={() => {
            if (!isLeaving) setIsLeaveConfirmOpen(false);
          }}
          onConfirm={handleConfirmLeave}
        />
      )}
    </>
  );
}
